<template>
    <v-card class="mb-4">
        <v-card-title class="subtitle-1 font-weight-medium py-2">
            <v-icon left>settings_input_component</v-icon>Comunicação RS-485
            <v-spacer></v-spacer>
            <v-chip small label dark :color="overall.color">
                <v-icon left small>{{ overall.icon }}</v-icon>{{ overall.text }}
            </v-chip>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="text-left">
            <div class="caption grey--text mb-2">
                Última janela de {{ windowMinutes }} min, enviada pelo gateway em {{ generatedAt }}. Esperado 0% de erro. Alarme de ruído com ao menos 50 leituras e 2 falhas quando CRC ≥ 0,5% ou erros ≥ 1%;
                de escravo sem resposta quando nenhuma leitura teve sucesso. Qualquer erro é destacado em laranja.
            </div>

            <v-alert v-for="alarm in alarms" :key="`${alarm.type}-${alarm.uuid}-${alarm.detail.slaveId}`" dense text :type="alarm.active ? 'error' : 'info'" class="mb-2">
                <strong>{{ alarmTitle(alarm) }}</strong> · {{ alarm.name }} · endereço {{ alarm.detail.slaveId }}<span v-if="boardNames({ uuid: alarm.uuid, slaveId: alarm.detail.slaveId })"> ({{ boardNames({ uuid: alarm.uuid, slaveId: alarm.detail.slaveId }) }})</span>
                <span v-if="!alarm.active">(normalizado)</span>
                <div class="caption">Início: {{ date(alarm.firstSeen) }} · Última ocorrência: {{ date(alarm.lastSeen) }} · Ocorrências: {{ alarm.occurrences }}</div>
            </v-alert>

            <v-data-table dense :headers="headers" :items="devices" item-key="key" :items-per-page="-1" hide-default-footer :mobile-breakpoint="0" no-data-text="Nenhuma leitura RS-485 registrada">
                <template v-slot:item.name="{ item }">
                    <div>{{ item.name }}</div>
                    <div class="caption grey--text">Endereço {{ item.slaveId }}<span v-if="item.boards"> · {{ item.boards }}</span></div>
                </template>
                <template v-slot:item.crcRate="{ item }">
                    <span :class="rateClass(item.lastWindow.reads, item.lastWindow.crc, item.crcRate, 0.005)">{{ percent(item.crcRate) }}</span>
                </template>
                <template v-slot:item.errorRate="{ item }">
                    <span :class="rateClass(item.lastWindow.reads, item.lastWindow.reads - item.lastWindow.ok, item.lastWindow.errorRate, 0.01)">{{ percent(item.lastWindow.errorRate) }}</span>
                </template>
                <template v-slot:item.totalErrorRate="{ item }">
                    {{ percent(item.total.errorRate) }} <span class="caption grey--text">({{ item.total.reads }})</span>
                </template>
                <template v-if="bus" v-slot:body.append>
                    <tr class="font-weight-medium">
                        <td>Barramento</td>
                        <td class="text-center">{{ bus.reads }}</td>
                        <td class="text-center">{{ bus.ok }}</td>
                        <td class="text-center">{{ bus.crc }}</td>
                        <td class="text-center">{{ bus.timeout }}</td>
                        <td class="text-center">{{ bus.other }}</td>
                        <td class="text-center">{{ percent(rate(bus.crc, bus.reads)) }}</td>
                        <td class="text-center">{{ percent(rate(bus.reads - bus.ok, bus.reads)) }}</td>
                        <td></td>
                    </tr>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<script>
// Diagnóstico gerado pelo protocol do gateway (Diagnostic.cpp) e enviado no check em info.diagnostic
const ALARMS = {
    RS485_NOISE: "Ruído na comunicação RS-485",
    RS485_OFFLINE: "Escravo RS-485 sem resposta",
};

const rate = (value, total) => (total > 0 ? value / total : 0);
const percent = (value) => `${((value || 0) * 100).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

export default {
    props: {
        diagnostics: {
            type: Array,
            default: () => [],
        },
        // info.chargers do gateway, usado para mostrar o nome das placas de cada endereço
        chargers: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            headers: [
                { text: "Dispositivo", value: "name", align: "left" },
                { text: "Leituras", value: "lastWindow.reads", align: "center" },
                { text: "OK", value: "lastWindow.ok", align: "center" },
                { text: "CRC", value: "lastWindow.crc", align: "center" },
                { text: "Timeout", value: "lastWindow.timeout", align: "center" },
                { text: "Outros", value: "lastWindow.other", align: "center" },
                { text: "Taxa CRC", value: "crcRate", align: "center" },
                { text: "Taxa erros", value: "errorRate", align: "center" },
                { text: "Erros (total)", value: "totalErrorRate", align: "center" },
            ],
        };
    },

    computed: {
        alarms() {
            return this.diagnostics
                .flatMap((d) => d.alarms || [])
                .filter((alarm) => ALARMS[alarm.type])
                .map((alarm) => ({ ...alarm, detail: alarm.detail || {} }))
                .sort((a, b) => b.active - a.active || (b.lastSeen > a.lastSeen ? 1 : -1));
        },
        devices() {
            return this.diagnostics
                .flatMap((d) => (d.rs485 && d.rs485.devices) || [])
                .map((device) => ({ ...device, key: `${device.uuid}-${device.slaveId}`, boards: this.boardNames(device), crcRate: rate(device.lastWindow.crc, device.lastWindow.reads) }));
        },
        bus() {
            let buses = this.diagnostics.map((d) => d.rs485 && d.rs485.bus).filter((bus) => bus);
            if (buses.length == 0) {
                return null;
            }
            return ["reads", "ok", "crc", "timeout", "other"].reduce((sum, key) => ({ ...sum, [key]: buses.reduce((s, bus) => s + (bus[key] || 0), 0) }), {});
        },
        overall() {
            if (this.alarms.some((alarm) => alarm.active)) {
                return { color: "red", icon: "error", text: "Com alarme" };
            }
            if (this.bus && this.bus.reads > this.bus.ok) {
                return { color: "orange", icon: "warning", text: "Com erros" };
            }
            return { color: "green", icon: "check_circle", text: "Sem erros" };
        },
        windowMinutes() {
            let d = this.diagnostics.find((d) => d.rs485);
            return d ? d.rs485.windowSeconds / 60 : 5;
        },
        generatedAt() {
            let dates = this.diagnostics.map((d) => d.generatedAt).sort();
            return dates.length > 0 ? this.date(dates[dates.length - 1]) : "-";
        },
    },

    methods: {
        rate,
        percent,
        boardNames(device) {
            const boards = (this.chargers && this.chargers[device.uuid] && this.chargers[device.uuid].boards) || {};
            return Object.keys(boards)
                .filter((name) => boards[name] == device.slaveId)
                .sort()
                .join(", ");
        },
        alarmTitle(alarm) {
            return ALARMS[alarm.type] || alarm.type;
        },
        date(iso) {
            return iso ? new Date(iso).toLocaleString("pt-BR") : "-";
        },
        // Mesmos limites do Diagnostic.cpp, qualquer erro é destacado pois o esperado é 0%
        rateClass(reads, errors, value, limit) {
            if (reads >= 50 && errors >= 2 && value >= limit) return "red--text font-weight-bold";
            return errors > 0 ? "orange--text text--darken-2 font-weight-medium" : "";
        },
    },
};
</script>
